import { useQuery } from '@apollo/client/react';
import { useCallback, useEffect } from 'react';

import { CandidateDocument, CandidateQuery } from '@pflegia/data-access/candidate.generated';
import { CandidateFilterType } from '@pflegia/types/api/candidate/candidate-filter-type';
import { FilterValue } from '@pflegia/types/api/job-board/filter-value';
import { useAuth } from '@pflegia/ui/contexts/auth.context';
import { extractCoordinatesFromLocation } from '@pflegia/util/common/location';

import { UseJobListFilterState } from '@shared/application/interfaces';

import { useFiltersLocalStorage } from './use-filters-local-storage';

interface FilterStateOptions {
  minimumFilters?: boolean;
  keepFilters?: boolean;
}

export const useFiltersState = ({ minimumFilters = false, keepFilters = false }: FilterStateOptions = {}): UseJobListFilterState => {
  const authenticated = useAuth();
  const { filters, setFilters: setFiltersBase, filtersFullyInitialized, setFiltersFullyInitialized } = useFiltersLocalStorage();

  const setFilters: (value: FilterValue[], markFullyInitialized?: boolean) => void = useCallback(
    (value, markFullyInitialized = false) => {
      const uniqueFilters = Array.from(new Map(value.map(filter => [`${filter.type}-${filter.value}`, filter])).values());
      setFiltersBase(uniqueFilters);
      if (markFullyInitialized) setFiltersFullyInitialized(true);
    },
    [setFiltersBase, setFiltersFullyInitialized],
  );

  const { data, loading } = useQuery(CandidateDocument, {
    skip: !authenticated || keepFilters,
    fetchPolicy: 'no-cache',
  });
  useEffect(() => {
    if (!data?.candidate) return;
    setFiltersBase(getFiltersValueFromCandidate(data.candidate));
    setFiltersFullyInitialized(true);
  }, [data?.candidate, setFiltersBase, setFiltersFullyInitialized]);

  const hasRequiredFilters = (validateMinimumFilters = minimumFilters): boolean => {
    if (validateMinimumFilters) return true;
    let hasPosition = false;
    let hasLocation = false;

    for (const { type } of filters) {
      if (type === CandidateFilterType.Location) {
        hasLocation = true;
      } else if (type === CandidateFilterType.Position || type === CandidateFilterType.JobPositionTitleQueries) {
        hasPosition = true;
      }
      if (hasLocation && hasPosition) return true;
    }

    return false;
  };

  return {
    filters,
    hasRequiredFilters,
    setFilters,
    isInitialized: authenticated && Boolean(data),
    isLoading: authenticated && loading,
    filtersFullyInitialized,
    setFiltersFullyInitialized,
  };
};

export const getFiltersValueFromCandidate = (candidate?: CandidateQuery['candidate']) => {
  if (!candidate) return [];
  const candidateLocations =
    // @ts-expect-error TODO: Fix type
    candidate.locations?.map((item: LocationObject & { location: { coordinates: number[] } }) => ({
      type: CandidateFilterType.Location,
      value: item.formattedAddress,
      locationObject: extractCoordinatesFromLocation(item),
    })) || [];

  const candidateEmploymentTypes =
    candidate.employmentTypes?.map(item => ({
      type: CandidateFilterType.Employment,
      value: item,
    })) || [];

  const candidateDepartments =
    candidate.departments?.map((item: { id: string }) => ({
      type: CandidateFilterType.Department,
      value: item.id,
    })) || [];

  const candidatePositions =
    candidate.jobTitleAnswers
      ?.filter(item => item.selected || item.isPending)
      ?.map(item => ({
        type: CandidateFilterType.Position,
        value: item?.jobTitle?.id,
        positionDetails: {
          trackingPriority: item.jobTitle.trackingPriority,
          selected: item.selected,
          isPending: item.isPending,
        },
      })) || [];

  const typeOfWorkFilter =
    candidate.typesOfWork?.map(item => ({
      type: CandidateFilterType.Facility,
      value: item,
    })) || [];

  const candidateTypeOfJob = candidate.permanentOrTemporary
    ? [{ type: CandidateFilterType.TypeOfJob, value: candidate.permanentOrTemporary }]
    : [];

  const newFilters = [
    ...candidateLocations,
    ...candidateEmploymentTypes,
    ...candidateDepartments,
    ...candidatePositions,
    ...typeOfWorkFilter,
    ...candidateTypeOfJob,
  ];
  return newFilters;
};
